/*
This component is a simple expander/collapser that uses CSS transitions and Javascript to animate the opening and closing of the content.

Animation height is determined by the scroll height of the content. This allows for the content to be any height and still animate correctly.

Example usage- each instance of the ExpanderCollapser needs an open button, close button, or both. WARNING- both open and close buttons are optional, so a developer could theoretically include neither, which would negate the purpose of using this component.

Example open and close buttons:
	const topButton = (
		<span>Show
			<i className='material-icons'>{'keyboard_arrow_down'}</i></span>
	);
	const bottomButton = (
		<span>Hide
			<i className='material-icons'>{'keyboard_arrow_up'}</i></span>
	);

Example usage for the toggleClass that is used to style the opening and closing buttons. These can be any HTML elements or group of elements- it is ultimately up to the designer to determine how the buttons should look.

// the parent toggle row
.expanderToggle {
	text-align: right;

	// the element that calls the handleToggleClick function
	>span {
		height: 20px;
		line-height: 20px;
		vertical-align: middle;
		cursor: pointer;

		// the content of the buttons
		span,
		i {
			height: 20px;
			line-height: 20px;
			vertical-align: middle;
		}
	}
}

-------------------------------
Full example. This example defaults to closed:

<ExpanderCollapser bottomButton={{initState: bottomButton}} topButton={{initState: topButton}} defaultOpen={false} toggleClass={styles.expanderToggle}>
	<div>Any child content here</div>
</ExpanderCollapser>

-------------------------------
Example without a close button, where the open button persists. This example defaults to closed:

<ExpanderCollapser topButton={{initState:topButton, persist: true}} defaultOpen={false} toggleClass={styles.expanderToggle}>
	<div>Any child content here</div>
</ExpanderCollapser>

-------------------------------
Example with topButton with two states that persists. This example defaults to closed:

const topButtonInit = (
	<span>{t('Click for more information')}<i className='material-icons'>{'keyboard_arrow_down'}</i></span>
);

const topButtonAlt = (
	<span>{t('Close')}<i className='material-icons'>{'keyboard_arrow_Up'}</i></span>
);

<ExpanderCollapser topButton={{initState:topButtonInit, altState:topButtonAlt, persist: true}} defaultOpen={false} toggleClass={styles.expanderToggle}>
	<div>Any child content here</div>
</ExpanderCollapser>

*/

interface expanderButton {
	// the initial way a button looks
	initState: React.ReactNode
	// the way a button looks after a user clicks it (this will only show if the button is set to persist)
	altState?: React.ReactNode
	// determines whether the button disappears once clicked, or remains in the component. The concept is that you could have an open button AND a closed button that don't persist. These would toggle between each other as the component is open and closed
	persist?: boolean
}

import classnames from 'classnames'
import * as React from 'react'

interface MyProps {
	// the amount of time the animation should take- defaults to 0.4s
	animationTime?: string
	// the content to be displayed in the expander/collapser
	children: React.ReactNode
	// the button component that opens/closes the content from below the content. This is of type expanderButton, which allows for an initState and an altState. This allows for one version of the button when content is open vs closed. Each state can be any HTML element or group of HTML elements
	bottomButton?: expanderButton
	// whether the content should be open or closed by default
	defaultOpen?: boolean
	// the button component that opens/closes the content from above the content. This is of type expanderButton, which allows for an initState and an altState. This allows for one version of the button when content is open vs closed. Each state can be any HTML element or group of HTML elements
	topButton?: expanderButton
	// this is a set of sass classes for styling the toggle row. It will usually contain a nested set of classes to style the outer toggle row and the inner open/close buttons. See example in the comments above
	toggleClass: string
}

import styles from './ExpanderCollapser.module.scss'

const ExpanderCollapser: React.FC<MyProps> = ({
	animationTime = '0.4s',
	bottomButton,
	children,
	defaultOpen = false,
	topButton,
	toggleClass,
}) => {
	// firstRun prevents an animation from occurring the first time the view loads
	// firstRun only needs to be set to false once
	const [firstRun, setFirstRun] = React.useState(true)
	const [contentOpen, setContentOpen] = React.useState(defaultOpen)
	const contentRef = React.useRef<HTMLDivElement>(null)

	const handleToggleClick = () => {
		if (contentOpen) {
			setContentOpen(false)
		} else {
			setContentOpen(true)
		}
	}

	React.useEffect(() => {
		if (firstRun) {
			setFirstRun(false)
			return
		}
		const expndDiv = contentRef.current
		if (expndDiv === null) return
		if (contentOpen) {
			expndDiv.style.height = expndDiv.scrollHeight + 'px'
		} else {
			expndDiv.style.height = '0px'
		}
	}, [contentOpen, firstRun])

	return (
		/* Styles for the toggle rows should come from the calling component- this allows for complete flexibility in styling both the toggle row and the topButton and bottomButton components */
		<>
			{/* open button */}
			{(topButton?.persist || !contentOpen) && (
				<div className={classnames(styles.toggleRow, toggleClass)}>
					{
						<span onClick={handleToggleClick}>
							{topButton?.altState && contentOpen
								? topButton?.altState
								: topButton?.initState}
						</span>
					}
				</div>
			)}
			<div
				ref={contentRef}
				className={styles.content}
				style={{ transition: `height ease-out ${animationTime}` }}
			>
				{children}
			</div>

			{/* close button (optional) */}
			{((contentOpen && bottomButton?.initState) ||
				(bottomButton?.initState && bottomButton?.persist)) && (
				<div className={classnames(styles.toggleRow, toggleClass)}>
					{
						<span onClick={handleToggleClick}>
							{bottomButton?.altState && contentOpen
								? bottomButton?.altState
								: bottomButton?.initState}
						</span>
					}
				</div>
			)}
		</>
	)
}

export default ExpanderCollapser
